import { EditOutlined } from '@ant-design/icons';
import { Button, Flex, Typography, Upload } from 'antd';
import { useTranslation } from 'react-i18next';

import Avatar from '@/UI/Avatar/Avatar';

import type { RcFile } from 'antd/lib/upload';

type ImageUploadFieldProps = {
  value?: string;
  onChange?: (value: string) => void;
  /** Display name for avatar fallback */
  name?: string;
  hint?: string;
  accept?: string;
  disabled?: boolean;
  onInvalidFormat?: () => void;
};

const DEFAULT_ACCEPT = 'image/png, image/jpeg, image/jpg';

const IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

const ImageUploadField = ({
  value,
  onChange,
  name,
  hint,
  accept = DEFAULT_ACCEPT,
  disabled,
  onInvalidFormat,
}: ImageUploadFieldProps) => {
  const { t } = useTranslation();

  const handleBeforeUpload = (file: RcFile): boolean => {
    if (IMAGE_MIME_TYPES.includes(file.type)) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result && typeof reader.result === 'string') {
          onChange?.(reader.result);
        }
      };
    } else {
      onInvalidFormat?.();
    }
    return false;
  };

  return (
    <Upload
      accept={accept}
      beforeUpload={handleBeforeUpload}
      showUploadList={false}
      disabled={disabled}
      styles={{
        root: {
          display: 'inline-block',
          width: 'auto',
          cursor: disabled ? 'not-allowed' : 'pointer',
        },
      }}
    >
      <Flex vertical align="start">
        <Flex align="end" gap={0}>
          <Avatar name={name} src={value} size="large" />
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            disabled={disabled}
          />
        </Flex>
        <Typography.Text type="secondary">
          {hint ?? t('PROFILE_IMAGE_FORMAT')}
        </Typography.Text>
      </Flex>
    </Upload>
  );
};

export default ImageUploadField;
